"use client"

import {ErrorBoundary, rollbarConfig} from "@soprisapps/components/components/utilities/ErrorBoundary";
import {Provider} from '@rollbar/react';
import {NotificationContainer as ReactNotificationContainer} from '@soprisapps/components/components/notifications/NotificationContainer';
import ClickableLink from "@soprisapps/components/components/utilities/ClickableLink";
import classNames from "classnames";
import {IView} from "@soprisapps/components/stores/InterfaceStore";
import Header from "@soprisapps/components/components/header/Header";
import {useGridType} from "@soprisapps/components/hooks/useGridType";
import {GridTypes} from "@soprisapps/components/pages/MainGridPage";
import {shouldShowBackgroundImage} from "@soprisapps/components/stores/StyleStore";
import {useFloatingBlocksThemeLayout} from "@soprisapps/components/hooks/useFloatingBlocksThemeLayout";
import {useStores} from "@soprisapps/components/stores/useStores";
import {useShowHomepage} from "@soprisapps/components/hooks/useShowHomepage";
import {useGetDevice} from "@soprisapps/components/hooks/useGetDevice";
import {observer} from "mobx-react";
import dynamic from "next/dynamic";
import {LocaleContextProvider} from "@soprisapps/components/utils/LocaleContext";

const pages = {
    [IView.DEFAULT]: dynamic(() => import("@soprisapps/components/pages/MainGridPage"), {loading: () => <></>}),
    [IView.ALPHA]: dynamic(() => import("@soprisapps/components/pages/AlphaCollectionPage"), {loading: () => <></>}),
    [IView.MAP]: dynamic(() => import("@soprisapps/components/pages/MapFixedCollectionPage"), {loading: () => <></>}),
    [IView.CLASSROOM]: dynamic(() => import("@soprisapps/components/pages/MainGridPage"), {loading: () => <></>}),
    [IView.CALENDAR]: dynamic(() => import("@soprisapps/components/pages/CalendarPage"), {loading: () => <></>}),
    [IView.MYCALENDAR]: dynamic(() => import("@soprisapps/components/pages/CalendarPage"), {loading: () => <></>}),
    [IView.PAGE]: dynamic(() => import("@soprisapps/components/pages/PageBlockPage/PageBlockPage"), {loading: () => <></>}),
    [IView.FEED]: dynamic(() => import("@soprisapps/components/pages/FeedGridPage"), {loading: () => <></>}),
    [IView.SEARCH]: dynamic(() => import("@soprisapps/components/pages/SearchPage/SearchPage"), {loading: () => <></>}),
    [IView.ERROR]: dynamic(() => import("@soprisapps/components/pages/ErrorPage"), {loading: () => <></>}),
    [IView.LOGIN]: dynamic(() => import("@soprisapps/components/pages/LoginPage"), {loading: () => <></>}),
    [IView.USER_SETTINGS]: dynamic(() => import("@soprisapps/components/pages/UserSettings/UserSettingsPage"), {loading: () => <></>}),
    [IView.ORGANIZATION_SETTINGS]: dynamic(() => import("@soprisapps/components/pages/OrganizationSettings/OrganizationSettings"), {loading: () => <></>}),
    [IView.ORGANIZATION_PAGE]: dynamic(() => import("@soprisapps/components/pages/ViewOrganization"), {loading: () => <></>}),
    [IView.SCHOOLFEED_PAGE]: dynamic(() => import("./SchoolFeedFullPage"), {loading: () => <></>}),
    [IView.SPLAINER_PAGE]: dynamic(() => import("@soprisapps/components/components/schoolfeed/SchoolFeedSplainerPage"), {loading: () => <></>}),
    [IView.NOTIFICATION]: dynamic(() => import("@soprisapps/components/pages/ViewNotificationPage"), {loading: () => <></>}),
    [IView.DASHBOARD]: dynamic(() => import("@soprisapps/components/pages/UserDashboard/UserDashboardPage"), {loading: () => <></>}),
    [IView.CONTENT]: dynamic(() => import("@soprisapps/components/pages/ContentPage"), {loading: () => <></>}),
}

const HeaderShortcuts = dynamic(() => import("@soprisapps/components/components/header/shortcuts/HeaderShortcuts").then(module => module.HeaderShortcuts), {loading: () => <></>});
const WebAppInstallPrompt = dynamic(() => import("@soprisapps/components/components/header/WebAppInstallPrompt").then(module => module.WebAppInstallPrompt), {loading: () => <></>});
const NotificationContainer = dynamic(() => import("@soprisapps/components/components/blocks/SchoolBlocks/Notification/NotificationContainer"), {loading: () => <></>});
const WelcomePage = dynamic(() => import("@soprisapps/components/pages/WelcomePage"), {loading: () => <></>});
const AdminMenu = dynamic(() => import("@soprisapps/components/components/misc/AdminMenu"), {loading: () => <></>});
const SchoolFeedWidget = dynamic(() => import("@soprisapps/components/components/schoolfeed/SchoolFeedWidget"), {loading: () => <></>});

const showAdminMenu = [IView.DEFAULT, IView.ALPHA, IView.MAP, IView.CLASSROOM, IView.CALENDAR, IView.PAGE, IView.ORGANIZATION_PAGE];

export const SchoolBlocksPageWrapper = observer((props) => {

    const {userStore, organizationStore, interfaceStore, i18nStore} = useStores();
    const floatingBlocksThemeLayout = useFloatingBlocksThemeLayout();

    function shouldShowFade() {
        // We want to show the background fade for all pages that are allowed to have a
        // background image, unless the client chooses Floating Blocks theme
        const validViewsFloatingBlocks = [IView.SCHOOLFEED_PAGE, IView.SEARCH];

        // If the theme is not Floating Blocks, check to ensure the page is not a Collection Page, and return true if
        // there is a background image
        if (!floatingBlocksThemeLayout) {
            return shouldShowBackgroundImage(interfaceStore.view) && !!organizationStore.currentOrganization.backgroundImage;
        } // If the theme is Floating Blocks, check if the view is either SchoolFeed, or Search and return true if
          // there is a background image
        else if (validViewsFloatingBlocks.includes(interfaceStore.view)) {
            return !!organizationStore.currentOrganization.backgroundImage;
        }
        return false;
    }

    const currentOrgGridType = useGridType();
    const titleOrgGridType = useGridType({useTitleOrg: true});

    const showSchoolFeedWidget = currentOrgGridType !== GridTypes.terraced &&
        (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "everyone" ||
            (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "admins" && userStore.isAdmin)) && interfaceStore.view !== IView.SCHOOLFEED_PAGE;

    let notifications = JSON.stringify(organizationStore.currentOrganization.alerts);
    if (interfaceStore.view === IView.NOTIFICATION) {
        // if user is here to view a notification, don't show any others
        notifications = JSON.stringify([]);
    }

    let content;
    const showHomePage = useShowHomepage()
    if (showHomePage) {
        content = <div id={"welcome-page-container"}>
            <WelcomePage />
        </div>
    } else {
        let ContentComponent = pages[interfaceStore.view];

        if (!ContentComponent) {
            ContentComponent = pages.error;
        } else if (interfaceStore.view === IView.ORGANIZATION_SETTINGS && !userStore.isAdmin) {
            ContentComponent = pages[IView.LOGIN];
        }

        const {isIOS, isAndroidChrome} = useGetDevice();

        const wrapperClassName = classNames({
            "isIOS": isIOS,
            "isAndroidChrome": isAndroidChrome,
            "isNotAndroidChrome": !isAndroidChrome,
            "isNotIOS": !isIOS,
            "sb-theme-simple_and_clean": !floatingBlocksThemeLayout,
            "sb-theme-floating_blocks": floatingBlocksThemeLayout,
            "sb-terraced-grid": currentOrgGridType === GridTypes.terraced && interfaceStore.isMainGrid,
            "sb-title-org-packery": titleOrgGridType === GridTypes.packery,
            "sb-title-org-terraced": titleOrgGridType === GridTypes.terraced,
            "sb-with-background-image": !!organizationStore.currentOrganization.backgroundImage,
        })

        const fadeBackgroundClassName = classNames({
            "fade-background": shouldShowFade(),
        })

        content = <div className={wrapperClassName}>
            <div className={fadeBackgroundClassName}>
                <div className={"sb-navigation-shown"}>
                    <div id={"sb-content-canvas"}>
            <span id="skiptocontent">
                <ClickableLink href="#sb-main-content" tabIndex={2}>skip to main content</ClickableLink>
                {showSchoolFeedWidget && <ClickableLink href={(interfaceStore.breakpoint === "break-point-lg") ? "#schoolfeed" : "#SchoolFeedMobileNav"} tabIndex={2}
                                                        onClick={(interfaceStore.breakpoint === "break-point-lg") ? () => interfaceStore.toggleSchoolFeedWidgetOpen() : undefined}>
                    skip to schoolfeed
                </ClickableLink>}
                <ClickableLink href={(interfaceStore.breakpoint === "break-point-lg") ? "#navigation" : "#MobileNavigationMenu"} tabIndex={2}>skip to navigation</ClickableLink>
            </span>
                        <ReactNotificationContainer/>
                        {organizationStore.organization.json_data?.settings?.webapp?.enabled && (interfaceStore.view !== IView.SPLAINER_PAGE) && <ErrorBoundary>
                            <WebAppInstallPrompt />
                        </ErrorBoundary>}
                        {notifications.length > 0 && <ErrorBoundary>
                            <NotificationContainer notifications={notifications}
                                                   shouldShowOverlay={true}
                                                   shouldShowBanner={false}
                                                   key={organizationStore.currentOrganization.id}/>
                        </ErrorBoundary>}
                        {(interfaceStore.isMainGrid || interfaceStore.view === IView.ORGANIZATION_PAGE) &&
                            <ErrorBoundary>
                                <HeaderShortcuts />
                            </ErrorBoundary>}
                        <ErrorBoundary>
                            <Header key={organizationStore.currentOrganization.id}/>
                        </ErrorBoundary>
                        {notifications.length > 0 && <ErrorBoundary>
                            <NotificationContainer notifications={notifications}
                                                   shouldShowBanner={true}
                                                   shouldShowOverlay={false}
                                                   key={organizationStore.currentOrganization.id}/>
                        </ErrorBoundary>}
                        <ErrorBoundary>
                            <ContentComponent />
                        </ErrorBoundary>
                        {showSchoolFeedWidget && <ErrorBoundary>
                            <SchoolFeedWidget organization_id={organizationStore.currentOrganization.id}/>
                        </ErrorBoundary>}
                        {userStore.editor && showAdminMenu.includes(interfaceStore.view) && <ErrorBoundary>
                            <AdminMenu key={interfaceStore.currentFullUrl} />
                        </ErrorBoundary>}
                    </div>
                </div>
            </div>
        </div>
    }

    if (userStore.id && rollbarConfig.payload) {
        rollbarConfig.payload.person = {
            id: userStore.id,
        }
    }

    return <Provider config={rollbarConfig}>
        <LocaleContextProvider locale={i18nStore.orgDefaultLocale}>
            {content}
        </LocaleContextProvider>
    </Provider>
})
